import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Chip,
  Avatar,
  Skeleton,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import "react-quill/dist/quill.snow.css";

import { getTicket, updateTicketStatus } from "../services/ticketService";
import { getStatuses } from "../services/statusService";
import { useUserRole } from "../context/UserRoleContext";

function TicketDetails() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { employee } = useUserRole();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadTicket = useCallback(async () => {
    try {
      setLoading(true);
      setNotFound(false);

      const data = await getTicket(ticketId);
      setTicket(data);
    } catch (error) {
      console.error("Ticket Details Error:", error.response?.data || error);

      if (error?.response?.status === 404) {
        setNotFound(true);
      } else {
        setSnackbar({
          open: true,
          message: "Failed to load ticket details",
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  useEffect(() => {
    getStatuses()
      .then(setStatuses)
      .catch((error) => console.error("Status List Error:", error));
  }, []);

  const canChangeStatus = Boolean(
    ticket && employee && ticket.assigned_to === employee.employee_id
  );

  const handleStatusChange = async (event) => {
    const statusName = event.target.value;

    if (!statusName || statusName === ticket.status || isUpdatingStatus) {
      return;
    }

    try {
      setIsUpdatingStatus(true);

      const updated = await updateTicketStatus(ticket.ticket_id, statusName, employee.employee_id);
      setTicket(updated);

      setSnackbar({
        open: true,
        message: "Ticket status updated successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Status Update Error:", error.response?.data || error);

      const message = error?.response?.data?.detail || "Failed to update status";

      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const priorityStyle = (priority) => {
    switch (priority) {
      case "Critical":
        return { background: "#fee2e2", color: "#dc2626" };
      case "High":
        return { background: "#ffedd5", color: "#ea580c" };
      case "Medium":
        return { background: "#fef3c7", color: "#ca8a04" };
      case "Low":
        return { background: "#dcfce7", color: "#16a34a" };
      default:
        return {};
    }
  };

  const statusStyle = (status) => {
    switch (status) {
      case "Open":
        return { background: "#fef3c7", color: "#d97706" };
      case "Assigned":
        return { background: "#dbeafe", color: "#2563eb" };
      case "In Progress":
        return { background: "#ede9fe", color: "#7c3aed" };
      case "Pending":
        return { background: "#fff7ed", color: "#c2410c" };
      case "Resolved":
        return { background: "#dcfce7", color: "#16a34a" };
      case "Done":
        return { background: "#d1fae5", color: "#047857" };
      case "Closed":
        return { background: "#e5e7eb", color: "#475569" };
      default:
        return {};
    }
  };

  const formatDate = (value) => {
    if (!value) {
      return "N/A";
    }

    return new Date(value).toLocaleString();
  };

  const detailRows = ticket
    ? [
        {
          label: "Status",
          icon: <InfoOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: canChangeStatus ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Select
                size="small"
                value={ticket.status || ""}
                onChange={handleStatusChange}
                disabled={isUpdatingStatus}
                sx={{ minWidth: 150, fontSize: 14 }}
              >
                {statuses.map((item) => (
                  <MenuItem key={item.status_id} value={item.status_name}>
                    {item.status_name}
                  </MenuItem>
                ))}
              </Select>

              {isUpdatingStatus && <CircularProgress size={16} />}
            </Box>
          ) : (
            <Chip
              label={ticket.status || "N/A"}
              size="small"
              sx={{ fontWeight: 600, ...statusStyle(ticket.status) }}
            />
          ),
        },
        {
          label: "Priority",
          icon: <FlagOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Chip
              label={ticket.priority || "N/A"}
              size="small"
              sx={{ fontWeight: 600, ...priorityStyle(ticket.priority) }}
            />
          ),
        },
        {
          label: "Category",
          icon: <CategoryOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Chip
              label={ticket.category || "N/A"}
              size="small"
              sx={{ background: "#f1f5f9", fontWeight: 600 }}
            />
          ),
        },
        {
          label: "Created By",
          icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar
                sx={{
                  width: 26,
                  height: 26,
                  fontSize: 12,
                  background: "#dbeafe",
                  color: "#2563eb",
                }}
              >
                {ticket.created_by?.charAt(0)}
              </Avatar>

              <Typography fontSize={14} color="text.primary">{ticket.created_by || "N/A"}</Typography>
            </Box>
          ),
        },
        {
          label: "Created Date",
          icon: <EventOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />,
          value: (
            <Typography fontSize={14} color="text.primary">
              {formatDate(ticket.created_at)}
            </Typography>
          ),
        },
      ]
    : [];

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        p: 3,
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/tickets")}
          sx={{
            textTransform: "none",
            color: "#475569",
            fontWeight: 600,
            borderRadius: 2,
            "&:hover": { background: "#eef2f7" },
          }}
        >
          Back to Tickets
        </Button>
      </Box>

      {loading && (
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box
            sx={{
              flex: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              p: 3,
            }}
          >
            <Skeleton variant="text" width="60%" height={36} />
            <Skeleton variant="rounded" height={200} sx={{ mt: 2 }} />
          </Box>

          <Box
            sx={{
              flex: 1,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              p: 3,
            }}
          >
            <Skeleton variant="text" width="50%" height={28} />
            <Skeleton variant="rounded" height={160} sx={{ mt: 2 }} />
          </Box>
        </Box>
      )}

      {!loading && notFound && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            p: 6,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: "50%",
              background: "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
            }}
          >
            <SearchOffRoundedIcon sx={{ fontSize: 48, color: "#94a3b8" }} />
          </Box>

          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1e293b", mb: 1 }}>
            Ticket Not Found
          </Typography>

          <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 3, maxWidth: 380 }}>
            This ticket doesn&apos;t exist or may have been deleted. Please check the link or go back to your ticket list.
          </Typography>

          <Button
            variant="contained"
            onClick={() => navigate("/tickets")}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            Back to Tickets
          </Button>
        </Box>
      )}

      {!loading && !notFound && ticket && (
        <>
          <Box
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              p: 3,
              mb: 3,
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, mb: 1 }}>
              TICKET OVERVIEW
            </Typography>

            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Box
                sx={{
                  background: "#eff6ff",
                  color: "#2563eb",
                  fontWeight: 700,
                  display: "inline-block",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                #{ticket.ticket_id}
              </Box>

              <Typography variant="h5" fontWeight={700} color="text.primary">
                {ticket.title}
              </Typography>

              <Chip
                label={ticket.status || "N/A"}
                size="small"
                sx={{ fontWeight: 600, ...statusStyle(ticket.status) }}
              />

              <Chip
                label={ticket.priority || "N/A"}
                size="small"
                sx={{ fontWeight: 600, ...priorityStyle(ticket.priority) }}
              />
            </Box>

            <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 1.2 }}>
              Created {formatDate(ticket.created_at)}
              {ticket.updated_at ? ` \u2022 Updated ${formatDate(ticket.updated_at)}` : ""}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 3,
              alignItems: "flex-start",
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {/* LEFT: DESCRIPTION */}
            <Box
              sx={{
                flex: 2,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                p: 3,
                minWidth: 0,
              }}
            >
              <Box display="flex" alignItems="center" gap={0.8} mb={1.5}>
                <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#2563eb" }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>
                  Description
                </Typography>
              </Box>

              {ticket.description ? (
                <Box
                  className="ql-editor"
                  sx={{
                    p: 2,
                    fontSize: 14,
                    color: "text.primary",
                    lineHeight: 1.7,
                    bgcolor: "background.default",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    "&.ql-editor": { overflow: "visible" },
                    "& ul, & ol": { paddingLeft: "1.4em", margin: "0 0 8px" },
                    "& li": { listStyle: "revert", paddingLeft: 0 },
                    "& li::before": { content: "none" },
                    "& ul > li": { listStyleType: "disc" },
                    "& ol > li": { listStyleType: "decimal" },
                  }}
                  dangerouslySetInnerHTML={{ __html: ticket.description }}
                />
              ) : (
                <Typography sx={{ fontSize: 14, color: "#94a3b8", fontStyle: "italic" }}>
                  No description provided.
                </Typography>
              )}
            </Box>

            {/* RIGHT: TICKET DETAILS */}
            <Box
              sx={{
                flex: 1,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                p: 3,
                minWidth: 260,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 1.5 }}>
                Ticket Details
              </Typography>

              <Box display="flex" flexDirection="column">
                {detailRows.map((row, index) => (
                  <React.Fragment key={row.label}>
                    <Box py={1.3}>
                      <Box display="flex" alignItems="center" gap={0.6} mb={0.7}>
                        {row.icon}
                        <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>
                          {row.label}
                        </Typography>
                      </Box>

                      {row.value}
                    </Box>

                    {index < detailRows.length - 1 && <Divider sx={{ borderColor: "divider" }} />}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </Box>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default TicketDetails;
